import { loadHtmlPartialScripts } from '../../../utils/loadHtmlPartialScripts'
import { focusTrap } from '../../../utils/focusTrap'
/**
 * Overlay
 */
export class GMXOverlay extends HTMLElement {
	overlayContainerEl: HTMLElement
	closeBtn: HTMLButtonElement
	focusTrap: (e: KeyboardEvent) => void

	static observedAttributes = ['data-show']

	private boundCloseBtnClickHandler = this._handleCloseBtnClick.bind(this)
	private boundRetryBtnClickHandler = this._handleRetryBtnClick.bind(this)

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (name === 'data-show' && oldValue !== newValue && this.hasAttribute('data-overlay-loaded')) {
			if (newValue !== null) {
				this.showOverlay()
			} else {
				this.hideOverlay()
			}
		}
	}

	get overlayType(): string {
		return this.getAttribute('data-overlay-type')
	}
	set overlayType(val: string) {
		this.setAttribute('data-overlay-type', val)
	}

	async connectedCallback(): Promise<void> {
		this.overlayContainerEl = this.querySelector('.overlay-container')
		this.closeBtn = this.querySelector('.close-btn')

		// For accessibility
		this.tabIndex = -1
		this.setAttribute('role', 'dialog')
		this.setAttribute('aria-modal', 'true')
		this.setAttribute('aria-label', 'modal') /* Todo:  This needs to be more dynamic but how??? */

		if (!this.overlayContainerEl && this.id) {
			// Custom element is empty, go fetch content
			await this.fetchAndInsertOverlayPartial()
		} else {
			this.setAttribute('data-overlay-loaded', 'true')
		}

		this.closeBtn?.addEventListener('click', this.boundCloseBtnClickHandler)

		this.focusTrap = focusTrap(this.overlayContainerEl)
		this.addEventListener('keydown', this.focusTrap)
	}

	disconnectedCallback(): void {
		this.closeBtn?.removeEventListener('click', this.boundCloseBtnClickHandler)
		this.querySelector('.gmx-expand-tile-retry-button')?.removeEventListener(
			'click',
			this.boundRetryBtnClickHandler
		)
		this.removeEventListener('keydown', this.focusTrap)
	}

	/**
	 * Callback to handle when the overlay close button is clicked.  Should initiate the hide behavior.
	 * @private
	 */
	private _handleCloseBtnClick(): void {
		this.hideOverlay()
	}

	/**
	 * Callback to handle when the retry button is clicked. Clears the error state and re-attempts the fetch.
	 * @private
	 */
	private _handleRetryBtnClick(): void {
		this.innerHTML = ''
		this.removeAttribute('data-overlay-loaded')
		this.overlayContainerEl = null
		this.closeBtn = null
		this.fetchAndInsertOverlayPartial()
	}

	/**
	 * Go out and fetch the overlay partial content and insert as a child of the component
	 */
	async fetchAndInsertOverlayPartial(): Promise<void> {
		try {
			const response = await fetch(this.id)
			if (!response.ok) {
				throw new Error()
			}

			const html = await response.text()
			this.innerHTML = html

			this.overlayContainerEl = this.querySelector('.overlay-container')
			this.closeBtn = this.querySelector('.close-btn')

			loadHtmlPartialScripts(this)

			this.setAttribute('data-overlay-loaded', '')

			if (this.hasAttribute('data-show')) {
				this.showOverlay()
			}
		} catch (error) {
			window.console.error(`ERROR:  Failed to load overlay partial: ${error}`)
			this._insertErrorLoadingMarkup()
			if (this.hasAttribute('data-show')) {
				this.showOverlay()
			}
		}
	}

	/**
	 * Method to be called if the content fails to be retrieved to let the user know there should be content here
	 *
	 * @private
	 */
	private _insertErrorLoadingMarkup(): void {
		const errorMrkup = `
			<div class="overlay-content">
				<div class="overlay-heading"></div>
				<div class="cta-container">
					<button class="gmx-btn gmx-btn-secondary icon-only close-btn" role="button" aria-label="close overlay" data-icon-name="close"></button>
				</div>
				<div class="overlay-content-body">
					<div class="gmx-loading-container">
						<div class="gmx-loading-spinner">
							<div class="gmx-loading-inner"></div>
						</div>
					</div>
					<div class="gmx-retry-error-wrapper">
						<div class="gmx-paragraph1 gmx-loading-error-message">Error loading content</div>
						<button class="gmx-btn gmx-btn-primary gmx-expand-tile-retry-button">Retry</button>
					</div>
				</div>
			</div>
		`

		const newOverlayContainer = document.createElement('div')
		newOverlayContainer.classList.add('overlay-container')
		newOverlayContainer.innerHTML = errorMrkup

		this.appendChild(newOverlayContainer)

		this.setAttribute('data-overlay-loaded', '')
		this.overlayContainerEl = this.querySelector('.overlay-container')
		this.closeBtn = this.querySelector('.close-btn')

		this.querySelector('.gmx-expand-tile-retry-button')?.addEventListener('click', this.boundRetryBtnClickHandler)
	}

	/**
	 * Method to show the overlay
	 */
	showOverlay(): void {
		if (window.browserDetectSingleton) {
			window.browserDetectSingleton.setScrollbarWidthOffset() /* Ensure layout doesn't shift */
		}

		document.body.classList.add('scroll-disabled')

		this.setAttribute('data-show', '')
		this.classList.add('overlay-open')
		this.closeBtn?.focus({ preventScroll: true })

		if (this.overlayType !== 'expand-from-tile') {
			/* To create the Scrim experience */
			this.style.backdropFilter = 'var(--blur-default)'
			this.style.backgroundColor = 'var(--colors-fill-dark-40)'

			if (this.overlayType === 'expanded-tile') {
				this.overlayContainerEl.style.transform = 'translateY(0%)'
			}
		}

		this.dispatchEvent(new CustomEvent('gmx-overlay-shown', { bubbles: true }))
	}

	/**
	 * Method to handle hiding the overlay.
	 */
	hideOverlay(): void {
		this.removeAttribute('data-show')

		if (this.overlayType !== 'expand-from-tile') {
			this.addEventListener(
				'transitionend',
				() => {
					this.classList.remove('overlay-open')
				},
				{ once: true }
			)

			this.overlayContainerEl.addEventListener(
				'transitionend',
				() => {
					document.body.classList.remove('scroll-disabled')
				},
				{ once: true }
			)

			if (this.overlayType === 'expanded-tile') {
				this.overlayContainerEl.style.transform = ''
			}

			/* For the Scrim */
			this.style.backdropFilter = 'var(--blur-none)'
			this.style.backgroundColor = ''
		} else {
			this.classList.remove('overlay-open')
		}
	}
}

if (!customElements.get('gmx-overlay')) {
	customElements.define('gmx-overlay', GMXOverlay)
}
